import React, { useState, useEffect, useCallback } from 'react';
import TopMenu from '../../contexts/TopMenu';
import '../../styles/Fulfillment.css';
import { authService } from '../../services/auth';
import { API_BASE_URL } from '../../config';
import { useNavigate } from 'react-router-dom';

function Fulfillment({ email, onLogout }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isTrackingModalOpen, setIsTrackingModalOpen] = useState(false);
  const [apiError, setApiError] = useState('');
  const [selectedTrackingInfo, setSelectedTrackingInfo] = useState(null);
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);
  const [selectedItemForTracking, setSelectedItemForTracking] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [selectedImage, setSelectedImage] = useState({});
  const [isDocumentLoading, setIsDocumentLoading] = useState({});
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
  const navigate = useNavigate();

  // Check admin status on component mount
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const adminStatus = await authService.checkAdminStatus();
        setIsAdmin(adminStatus);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, []);

  // Function to toggle expanded item view
  const toggleExpandItem = (itemIndex) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemIndex]: !prev[itemIndex]
    }));
  };

  // Function to select image for gallery view
  const selectImage = (itemIndex, imageUrl) => {
    setSelectedImage(prev => ({
      ...prev,
      [itemIndex]: imageUrl
    }));
  };

  // New function to update tracking information
  const updateTrackingInfo = async (orderId) => {
    try {
      const authKey = localStorage.getItem('authKey') || '';
      const userId = localStorage.getItem('userId') || '';
      
      if (!authKey || !userId) {
        console.error('Authentication credentials not found');
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/brands/orders/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auth_key: authKey,
          user_id: userId,
          order_id: orderId
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        console.error('Failed to update tracking info:', data.message);
        return;
      }
      
      console.log('Tracking info updated:', data);
      
      // Refresh orders list without showing loading screen
      await fetchOrders(false);
    } catch (error) {
      console.error('Error updating tracking info:', error);
    }
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');

  // Fetch available warehouses
  const fetchWarehouses = useCallback(async () => {
    try {
      setIsLoadingLocations(true);
      const authKey = localStorage.getItem('authKey') || '';
      const userId = localStorage.getItem('userId') || '';
      
      if (authKey && userId) {
        try {
          const postResponse = await fetch(`${API_BASE_URL}/brands/warehouses/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              auth_key: authKey,
              user_id: userId
            })
          });
          
          const postResult = await postResponse.json();
          
          if (postResponse.ok && Array.isArray(postResult.data)) {
            setWarehouses(postResult.data);
            if (postResult.data && postResult.data.length > 0) {
              setSelectedWarehouse(postResult.data[0]);
            }
            return;
          }
        } catch (postError) {
          console.error('POST request failed, trying GET:', postError);
        }
      }
      
      if (email) {
        const getResponse = await fetch(`${API_BASE_URL}/brands/warehouses?email=${encodeURIComponent(email)}`);
        const getResult = await getResponse.json();
        
        if (getResponse.ok && Array.isArray(getResult.data)) {
          setWarehouses(getResult.data);
          if (getResult.data && getResult.data.length > 0) {
            setSelectedWarehouse(getResult.data[0]);
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch warehouses:', err);
    } finally {
      setIsLoadingLocations(false);
    }
  }, [email]);

  const fetchOrders = useCallback(async (showLoading = true, page = currentPage, status = statusFilter) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const response = await authService.getOrders({
        page,
        limit: 10,
        status: status !== 'all' ? status : undefined
      });
      setOrders(response.data || []);
      if (response.pagination) {
        setCurrentPage(response.pagination.current_page || page);
        setTotalPages(response.pagination.total_pages || 1);
        setTotalItems(response.pagination.total_items || (response.data ? response.data.length : 0));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [currentPage, statusFilter]);

  useEffect(() => {
    fetchOrders(true, currentPage, statusFilter);
    fetchWarehouses();
  }, [fetchOrders, fetchWarehouses, currentPage, statusFilter]);

  const handleAddTracking = (order) => {
    setSelectedOrder(order);
    setSelectedWarehouse(null);
    fetchWarehouses();
    setIsConfirmModalOpen(true);
  };

  const handleViewTrackingInfo = (order, e) => {
    e.stopPropagation();
    
    // Update tracking info when View Tracking is clicked
    updateTrackingInfo(order.order_id);
    
    // Instead of finding just one item, we'll pass the entire order
    // so the user can select which item to track
    setSelectedOrder(order);
    setSelectedItemForTracking(null); // Reset selected item
    setIsTrackingModalOpen(true);
  };

  const selectItemForTracking = (item) => {
    if (item && item.shipping_info) {
      setSelectedTrackingInfo(item.shipping_info);
      setSelectedItemForTracking(item);
    }
  };

  const checkForShippingInfo = (order) => {
    return order.items?.some(item => item.shipping_info);
  };

  const getDeliveredItemsCount = (order) => {
    return order.items?.filter(item => item.delivered_details)?.length || 0;
  };

  const handleConfirmSchedule = async () => {
    try {
      setApiError('');
      setIsConfirmLoading(true);
      // Get auth credentials from localStorage
      const authKey = localStorage.getItem('authKey') || '';
      const userId = localStorage.getItem('userId') || '';
      
      if (!authKey || !userId) {
        throw new Error('Authentication credentials not found');
      }
      
      // Validate warehouse selection
      if (!selectedWarehouse) {
        throw new Error('Please select a warehouse for pickup');
      }
      
      const response = await fetch(`${API_BASE_URL}/brands/orders/ship`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auth_key: authKey,
          user_id: userId,
          warehouse_id: selectedWarehouse.warehouse_id,
          order_id: selectedOrder.order_id,
          process_orders: true
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to schedule pickup');
      }

      // Refresh orders list without showing loading screen
      await fetchOrders(false);
      setIsConfirmModalOpen(false);
    } catch (error) {
      console.error('Error scheduling pickup:', error);
      setApiError(error.message || 'Failed to schedule pickup');
    } finally {
      setIsConfirmLoading(false);
    }
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
    
    // Update tracking info when order modal is opened
    updateTrackingInfo(order.order_id);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Function to fetch shipping documents
  const fetchShippingDocuments = async (orderId, shiprocketOrderId, shiprocketShipmentId) => {
    try {
      setIsDocumentLoading(prev => ({ ...prev, [shiprocketShipmentId]: true }));
      const authKey = localStorage.getItem('authKey') || '';
      const userId = localStorage.getItem('userId') || '';
      
      if (!authKey || !userId) {
        console.error('Authentication credentials not found');
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/brands/orders/${orderId}/label/${shiprocketOrderId}/${shiprocketShipmentId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auth_key: authKey,
          user_id: userId
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error('Failed to fetch shipping documents');
      }

      // Update the order to include shipping_documents
      const updatedOrders = orders.map(order => {
        if (order.order_id === orderId) {
          return {
            ...order,
            items: order.items.map(item => {
              if (item.shipping_info?.shiprocket_shipment_id === shiprocketShipmentId) {
                return {
                  ...item,
                  shipping_info: {
                    ...item.shipping_info,
                    shipping_documents: {
                      manifest_url: data.data.manifest_url,
                      label_url: data.data.label_url,
                      invoice_url: data.data.invoice_url
                    }
                  }
                };
              }
              return item;
            })
          };
        }
        return order;
      });
      setOrders(updatedOrders);

      // Update tracking information to refresh the data
      await updateTrackingInfo(orderId);
      
    } catch (error) {
      console.error('Error fetching shipping documents:', error);
      setApiError('Failed to fetch shipping documents');
    } finally {
      setIsDocumentLoading(prev => ({ ...prev, [shiprocketShipmentId]: false }));
    }
  };

  // Helper function to download document
  const downloadDocument = (url, fileName) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleAdminAction = (order) => {
    setSelectedOrder(order);
    setIsAdminModalOpen(true);
  };

  // Add this function to handle item click
  const handleItemClick = (orderId, itemHandle, itemVariantId) => {
    navigate(`/orders/${orderId}/${itemHandle}/${itemVariantId}`);
  };

  if (loading) {
    return (
      <div className="fulfillment-loading">
        <div className="loader"></div>
        <p>Loading orders...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fulfillment-error">
        <p>{error}</p>
        <button onClick={fetchOrders}>Retry</button>
      </div>
    );
  }

  return (
    <div className="dashboard-root">
      <TopMenu pageTitle="Order Fulfillment" email={email} onLogout={onLogout} />
      <div className="dashboard-container">
        <div className="fulfillment">
          <div style={{
            display: 'flex',
            justify: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            flexWrap: 'wrap',
            gap: '15px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Filter Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  padding: '8px 12px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontSize: '13px',
                  backgroundColor: '#fff',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Orders</option>
                <option value="confirmed">Confirmed / Placed</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            {totalItems > 0 && (
              <span style={{ fontSize: '13px', color: '#666' }}>
                Showing {orders.length} of {totalItems} orders
              </span>
            )}
          </div>

          <div className="orders-table">
            <table>
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Total</th>
                  <th>Tracking</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders
                  .map((order) => {
                    const customerName = order.shipping_address?.name ||
                      (order.shipping_address?.first_name ? `${order.shipping_address.first_name} ${order.shipping_address.last_name || ''}` : null) ||
                      order.customer?.name ||
                      order.customer_name ||
                      'N/A';
                    const orderTotal = order.order_summary?.total ?? order.total_price ?? order.total_amount ?? order.total ?? 0;
                    return (
                    <tr key={order.id || order.order_id} onClick={() => handleOrderClick(order)}>
                      <td>{order.order_id}</td>
                      <td>{formatDate(order.created_at)}</td>
                      <td>{customerName}</td>
                      <td>₹{Number(orderTotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td>
                        {order.tracking_info && order.tracking_info.length > 0 ? (
                          <div className="tracking-info">
                            {order.tracking_info.map((tracking, index) => (
                              <a
                                key={index}
                                href={tracking.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  textDecoration: 'none',
                                  color: '#000',
                                  fontFamily: 'Helvetica, Arial, sans-serif',
                                  fontWeight: '300',
                                  fontSize: '13px',
                                  borderBottom: '1px solid #000',
                                  padding: '0 0 2px 0',
                                  letterSpacing: '0.5px'
                                }}
                              >
                                {tracking.number}
                              </a>
                            ))}
                          </div>
                        ) : checkForShippingInfo(order) ? (
                          <button
                            className="view-tracking-btn"
                            onClick={(e) => handleViewTrackingInfo(order, e)}
                            style={{
                              padding: '8px 15px',
                              background: '#fff',
                              color: '#000',
                              border: '1px solid #000',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontFamily: 'Helvetica, Arial, sans-serif',
                              letterSpacing: '1px',
                              textTransform: 'uppercase',
                              fontWeight: '400',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {getDeliveredItemsCount(order) > 0 
                              ? `${getDeliveredItemsCount(order)} ${getDeliveredItemsCount(order) === 1 ? 'Item' : 'Items'} Delivered` 
                              : 'View Tracking'}
                          </button>
                        ) : (
                          <span className="no-tracking" style={{
                            fontFamily: 'Helvetica, Arial, sans-serif',
                            fontSize: '13px',
                            color: '#666',
                            fontWeight: '300'
                          }}>
                            No tracking
                          </span>
                        )}
                      </td>
                      <td>
                        {(!order.tracking_info || order.tracking_info.length === 0) && !checkForShippingInfo(order) && (
                          <button
                            className="add-tracking-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddTracking(order);
                            }}
                            style={{
                              padding: '8px 15px',
                              background: '#000',
                              color: '#fff',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontFamily: 'Helvetica, Arial, sans-serif',
                              letterSpacing: '1px',
                              textTransform: 'uppercase',
                              fontWeight: '400',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            Schedule Pickup
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            className="add-tracking-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAdminAction(order);
                            }}
                            style={{
                              padding: '8px 15px',
                              background: '#fff',
                              color: '#000',
                              border: '1px solid #000',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontFamily: 'Helvetica, Arial, sans-serif',
                              letterSpacing: '1px',
                              textTransform: 'uppercase',
                              fontWeight: '400',
                              transition: 'all 0.2s ease',
                              marginLeft: '10px'
                            }}
                          >
                            Admin Action
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                
                {!orders.length && (
                  <tr>
                    <td colSpan="6">
                      <div style={{
                        padding: '60px 0',
                        textAlign: 'center',
                        fontFamily: 'Helvetica, Arial, sans-serif',
                      }}>
                        <div style={{
                          fontSize: '48px',
                          marginBottom: '20px',
                        }}>
                          📦
                        </div>
                        <h3 style={{
                          fontSize: '20px',
                          fontWeight: '400',
                          margin: '0 0 10px 0',
                          letterSpacing: '0.5px',
                        }}>
                          No orders to fulfill yet
                        </h3>
                        <p style={{
                          fontSize: '14px',
                          color: '#666',
                          fontWeight: '300',
                          maxWidth: '400px',
                          margin: '0 auto 20px',
                          lineHeight: '1.5',
                        }}>
                          When you receive new orders, they'll appear here ready for fulfillment.
                        </p>
                        <a 
                          href="/products" 
                          style={{
                            padding: '8px 16px',
                            background: '#000',
                            color: '#fff',
                            textDecoration: 'none',
                            fontSize: '11px',
                            fontFamily: 'Helvetica, Arial, sans-serif',
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            fontWeight: '400',
                            display: 'inline-block',
                            transition: 'all 0.2s ease',
                          }}
                        >
                          Manage Products
                        </a>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                justify: 'center',
                alignItems: 'center',
                gap: '15px',
                marginTop: '20px',
                padding: '10px 0'
              }}>
                <button
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: currentPage <= 1 ? '#eee' : '#000',
                    color: currentPage <= 1 ? '#aaa' : '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Previous
                </button>
                <span style={{ fontSize: '13px', color: '#333' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: currentPage >= totalPages ? '#eee' : '#000',
                    color: currentPage >= totalPages ? '#aaa' : '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {isModalOpen && selectedOrder && (
            <div className="modal-overlay" onClick={(e) => {
              if (e.target === e.currentTarget) {
                setIsModalOpen(false);
              }
            }}>
              <div 
                className="modal-content" 
                style={{ 
                  maxWidth: '95%', 
                  width: '1600px', 
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  position: 'relative',
                  padding: '0px',
                  animation: 'slideIn 0.3s ease-out'
                }}
              >
                <div className="modal-header">
                  <h3 className="modal-title">{`Order Details - ${selectedOrder.order_id}`}</h3>
                  <button className="modal-close-btn" onClick={() => setIsModalOpen(false)} aria-label="Close">
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  <div className="wide-order-details-grid" style={{ display: 'flex', gap: '30px', alignItems: 'stretch' }}>
                    {/* Left column: Customer Info + Shipping Address */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '30px' }}>
                      <div className="detail-section" style={{ flex: 'unset' }}>
                        <h4>Customer Information</h4>
                        {selectedOrder.shipping_address ? (
                          <>
                            <p>Name: {selectedOrder.shipping_address.name || ''}</p>
                            <p>Phone: {selectedOrder.shipping_address.phone || 'N/A'}</p>
                            <p>Customer ID: {selectedOrder.customer_id || 'N/A'}</p>
                          </>
                        ) : (
                          <p>No customer information available</p>
                        )}
                      </div>
                      <div className="detail-section" style={{ flex: 'unset' }}>
                        <h4>Shipping Address</h4>
                        <p>{selectedOrder.shipping_address?.name || ''}</p>
                        <p>{selectedOrder.shipping_address?.address1 || ''}</p>
                        <p>{selectedOrder.shipping_address?.address2 || ''}</p>
                        <p>{selectedOrder.shipping_address?.city || ''} {selectedOrder.shipping_address?.pincode || ''}</p>
                        <p>{selectedOrder.shipping_address?.state || ''}</p>
                      </div>
                    </div>
                    {/* Right column: Payment Summary */}
                    <div className="detail-section" style={{ flex: 1, alignSelf: 'stretch' }}>
                      <h4>Payment Summary</h4>
                      <p>Order ID: {selectedOrder.order_id}</p>
                      <p>Fastrr Order ID: {selectedOrder.fastrr_order_id || 'N/A'}</p>
                      <p>Payment Method: {selectedOrder.payment_details?.payment_method || 'N/A'}</p>
                      <p>Amount: ₹{selectedOrder.payment_details?.amount || '0'}</p>
                      <p>Currency: {selectedOrder.payment_details?.currency || 'INR'}</p>
                      <p>Payment Status: {selectedOrder.payment_details?.status || 'Unknown'}</p>
                      {selectedOrder.payment_details?.coupon && (
                        <>
                          <p>Coupon Code: {selectedOrder.payment_details.coupon.code}</p>
                          <p>Discount: ₹{selectedOrder.payment_details.coupon.discount}</p>
                        </>
                      )}
                      <p>Subtotal: ₹{selectedOrder.order_summary?.subtotal || '0'}</p>
                      <p>Shipping: ₹{selectedOrder.order_summary?.shipping || '0'}</p>
                      <p>Tax: ₹{selectedOrder.order_summary?.tax || '0'}</p>
                      <p>Total: ₹{selectedOrder.order_summary?.total || '0'}</p>
                      <p>Fulfillment Status: {selectedOrder.status || 'Unfulfilled'}</p>
                      {selectedOrder.note && <p>Note: {selectedOrder.note}</p>}
                    </div>
                  </div>
                  {/* Restore Order Items section below the details grid */}
                  <div className="detail-section" style={{ marginTop: '40px' }}>
                    <h4>Order Items</h4>
                    <div className="wide-order-items" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
                      {selectedOrder.items && selectedOrder.items.map((item, index) => (
                        <div key={index} className="order-item" style={{
                          marginBottom: '25px',
                          padding: '20px',
                          border: '1px solid #eee',
                          borderRadius: '4px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '20px'
                        }}>
                          {/* Main Item Info Row */}
                          <div style={{ display: 'flex', gap: '20px' }}>
                            {/* Product Image */}
                            <div style={{ flexShrink: 0, width: '100px', height: '100px' }}>
                              {item.image ? (
                                <img 
                                  src={selectedImage[index] || item.image} 
                                  alt={item.name}
                                  style={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    objectFit: 'cover',
                                    borderRadius: '4px' 
                                  }}
                                />
                              ) : (
                                <div style={{ 
                                  width: '100%', 
                                  height: '100%', 
                                  backgroundColor: '#f5f5f5', 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center',
                                  borderRadius: '4px',
                                  color: '#aaa',
                                  fontSize: '11px'
                                }}>
                                  No Image
                                </div>
                              )}
                            </div>
                            {/* Product Details */}
                            <div style={{ flex: 1 }}>
                              <p style={{ 
                                fontSize: '16px', 
                                fontWeight: '500', 
                                margin: '0 0 8px 0' 
                              }}>
                                {item.name || 'Unknown Product'}
                              </p>
                              <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: '10px',
                                fontSize: '14px',
                                fontWeight: '300',
                                margin: '0 0 12px 0'
                              }}>
                                <p style={{ margin: '0', color: '#666' }}>
                                  <span style={{ fontWeight: '400' }}>Variant:</span> {item.variant_title || 'Default'}
                                </p>
                                <p style={{ margin: '0', color: '#666' }}>
                                  <span style={{ fontWeight: '400' }}>Quantity:</span> {item.quantity || 0}
                                </p>
                                <p style={{ margin: '0', color: '#666' }}>
                                  <span style={{ fontWeight: '400' }}>Price:</span> ₹{item.price || '0'}
                                </p>
                                {item.product && item.product.vendor && (
                                  <p style={{ margin: '0', color: '#666' }}>
                                    <span style={{ fontWeight: '400' }}>Brand:</span> {item.product.vendor}
                                  </p>
                                )}
                              </div>
                              {/* Basic Product Info */}
                              {item.product && (
                                <div style={{ 
                                  fontSize: '13px', 
                                  borderTop: '1px solid #f5f5f5',
                                  paddingTop: '10px',
                                  marginTop: '5px' 
                                }}>
                                  {item.product.product_type && (
                                    <p style={{ margin: '0 0 5px 0', color: '#666' }}>
                                      <span style={{ fontWeight: '400' }}>Type:</span> {item.product.product_type}
                                    </p>
                                  )}
                                </div>
                              )}
                              {item.shipping_info && (
                                <div style={{ 
                                  fontSize: '12px', 
                                  marginTop: '10px',
                                  padding: '5px 8px',
                                  backgroundColor: '#f8f8f8',
                                  borderRadius: '3px',
                                  display: 'inline-block'
                                }}>
                                  <p style={{ margin: '0', color: '#666' }}>
                                    <span style={{ fontWeight: '400' }}>Shipping Status:</span> Shipped
                                  </p>
                                </div>
                              )}
                            </div>
                            {/* View Details Button */}
                            <div>
                              <button
                                onClick={() => toggleExpandItem(index)}
                                style={{
                                  backgroundColor: 'transparent',
                                  border: '1px solid #eee',
                                  borderRadius: '4px',
                                  padding: '8px 12px',
                                  fontSize: '11px',
                                  fontFamily: 'Helvetica, Arial, sans-serif',
                                  letterSpacing: '1px',
                                  textTransform: 'uppercase',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                {expandedItems[index] ? 'Hide Details' : 'View Details'}
                              </button>
                            </div>
                          </div>
                          {/* Expanded View */}
                          {expandedItems[index] && item.product && (
                            <div style={{
                              borderTop: '1px solid #eee',
                              paddingTop: '15px',
                              marginTop: '5px'
                            }}>
                              {/* Image Gallery */}
                              {item.product.images && item.product.images.length > 1 && (
                                <div>
                                  <p style={{ 
                                    fontSize: '12px', 
                                    fontWeight: '500', 
                                    textTransform: 'uppercase', 
                                    letterSpacing: '1px',
                                    margin: '0 0 10px 0'
                                  }}>
                                    Product Images
                                  </p>
                                  <div style={{ 
                                    display: 'flex', 
                                    gap: '10px', 
                                    overflowX: 'auto',
                                    paddingBottom: '10px'
                                  }}>
                                    {item.product.images.slice(0, 6).map((image, imgIndex) => (
                                      <div 
                                        key={imgIndex}
                                        onClick={() => selectImage(index, image.src)}
                                        style={{
                                          width: '60px',
                                          height: '60px',
                                          flexShrink: 0,
                                          cursor: 'pointer',
                                          border: selectedImage[index] === image.src ? '2px solid #000' : '1px solid #eee',
                                          borderRadius: '4px',
                                          padding: '2px'
                                        }}
                                      >
                                        <img 
                                          src={image.src} 
                                          alt={`${item.name} view ${imgIndex + 1}`}
                                          style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            borderRadius: '2px'
                                          }}
                                        />
                                      </div>
                                    ))}
                                    {item.product.images.length > 6 && (
                                      <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '60px',
                                        height: '60px',
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: '4px',
                                        color: '#666',
                                        fontSize: '12px'
                                      }}>
                                        +{item.product.images.length - 6} more
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              {/* Product Description */}
                              {item.product.body_html && (
                                <div style={{ marginTop: '15px' }}>
                                  <p style={{ 
                                    fontSize: '12px', 
                                    fontWeight: '500', 
                                    textTransform: 'uppercase', 
                                    letterSpacing: '1px',
                                    margin: '0 0 10px 0'
                                  }}>
                                    Product Description
                                  </p>
                                  <div 
                                    style={{ 
                                      fontSize: '13px', 
                                      lineHeight: '1.6',
                                      color: '#666',
                                      fontWeight: '300'
                                    }}
                                    dangerouslySetInnerHTML={{ __html: item.product.body_html }}
                                  />
                                </div>
                              )}
                              {/* Advanced Product Information */}
                              {item.product.visual_attributes && (
                                <div style={{ marginTop: '15px' }}>
                                  <p style={{ 
                                    fontSize: '12px', 
                                    fontWeight: '500', 
                                    textTransform: 'uppercase', 
                                    letterSpacing: '1px',
                                    margin: '0 0 10px 0'
                                  }}>
                                    Product Attributes
                                  </p>
                                  <div style={{ 
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                    gap: '10px',
                                    fontSize: '13px',
                                    color: '#666'
                                  }}>
                                    {item.product.visual_attributes.style && (
                                      <div>
                                        <p style={{ margin: '0 0 3px 0', fontWeight: '400' }}>Style</p>
                                        <p style={{ margin: '0', fontWeight: '300' }}>
                                          {item.product.visual_attributes.style.primary}
                                          {item.product.visual_attributes.style.specific && 
                                            ` - ${item.product.visual_attributes.style.specific}`}
                                        </p>
                                      </div>
                                    )}
                                    {item.product.visual_attributes.occasion && (
                                      <div>
                                        <p style={{ margin: '0 0 3px 0', fontWeight: '400' }}>Occasion</p>
                                        <p style={{ margin: '0', fontWeight: '300' }}>
                                          {item.product.visual_attributes.occasion.primary}
                                          {item.product.visual_attributes.occasion.specific && 
                                            ` - ${item.product.visual_attributes.occasion.specific}`}
                                        </p>
                                      </div>
                                    )}
                                    {item.product.visual_attributes.pattern && (
                                      <div>
                                        <p style={{ margin: '0 0 3px 0', fontWeight: '400' }}>Pattern</p>
                                        <p style={{ margin: '0', fontWeight: '300' }}>
                                          {item.product.visual_attributes.pattern.primary}
                                          {item.product.visual_attributes.pattern.specific && 
                                            ` - ${item.product.visual_attributes.pattern.specific}`}
                                        </p>
                                      </div>
                                    )}
                                    {item.product.visual_attributes.colors && (
                                      <div>
                                        <p style={{ margin: '0 0 3px 0', fontWeight: '400' }}>Color</p>
                                        <p style={{ margin: '0', fontWeight: '300' }}>
                                          {item.product.visual_attributes.colors.primary_color}
                                          {item.product.visual_attributes.colors.palette && 
                                            ` (${item.product.visual_attributes.colors.palette} palette)`}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              {/* Variant Details */}
                              {item.product.variants && item.product.variants.length > 0 && (
                                <div style={{ marginTop: '15px' }}>
                                  <p style={{ 
                                    fontSize: '12px', 
                                    fontWeight: '500', 
                                    textTransform: 'uppercase', 
                                    letterSpacing: '1px',
                                    margin: '0 0 10px 0'
                                  }}>
                                    Available Variants
                                  </p>
                                  <div style={{ 
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '8px',
                                  }}>
                                    {item.product.variants.map((variant, varIndex) => (
                                      <div 
                                        key={varIndex}
                                        style={{
                                          padding: '8px 12px',
                                          border: '1px solid #eee',
                                          borderRadius: '4px',
                                          fontSize: '12px',
                                          backgroundColor: item.variant_id === `${item.product.external_product_id}_${variant.option1}` ? '#f5f5f5' : 'transparent'
                                        }}
                                      >
                                        {variant.option1}
                                        {variant.price && ` - ₹${variant.price}`}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {isConfirmModalOpen && selectedOrder && (
            <div className="modal-overlay" onClick={(e) => {
              if (e.target === e.currentTarget) {
                setIsConfirmModalOpen(false);
              }
            }}>
              <div 
                className="modal-content" 
                style={{ 
                  maxWidth: '90%', 
                  width: '600px', 
                  height: 'auto',
                  maxHeight: '90vh',
                  padding: '0px',
                  overflowY: 'auto',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  position: 'relative',
                  animation: 'slideIn 0.3s ease-out'
                }}
              >
                <div className="modal-header">
                  <h3 className="modal-title">Confirm Schedule Pickup</h3>
                  <button className="modal-close-btn" onClick={() => setIsConfirmModalOpen(false)} aria-label="Close">
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  <p style={{ 
                    marginBottom: '15px',
                    fontSize: '16px',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontWeight: '300'
                  }}>
                    Are you sure you want to schedule pickup for order #{selectedOrder.order_id}?
                  </p>
                  
                  {/* Warehouse Selection Section */}
                  <div style={{ marginBottom: '20px' }}>
                    <p style={{ 
                      margin: '0 0 15px 0', 
                      fontWeight: '500', 
                      fontSize: '14px'
                    }}>
                      Select Warehouse for Pickup
                    </p>
                    
                    {isLoadingLocations ? (
                      <div style={{ 
                        padding: '20px', 
                        textAlign: 'center', 
                        backgroundColor: '#f9f9f9',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px'
                      }}>
                        <span style={{ 
                          width: '16px', 
                          height: '16px', 
                          border: '2px solid rgba(0,0,0,0.3)', 
                          borderTop: '2px solid #000',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          display: 'inline-block'
                        }}></span>
                        Loading warehouses...
                      </div>
                    ) : warehouses.length > 0 ? (
                      <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {warehouses.map((warehouse) => {
                          // Create a unique identifier for each warehouse
                          const warehouseId = warehouse._id || warehouse.warehouse_id;
                          const selectedWarehouseId = selectedWarehouse ? 
                            (selectedWarehouse._id || selectedWarehouse.warehouse_id) : null;
                          const isSelected = warehouseId === selectedWarehouseId;
                          
                          return (
                            <div 
                              key={warehouseId}
                              onClick={() => setSelectedWarehouse(warehouse)}
                              style={{
                                padding: '15px',
                                marginBottom: '10px',
                                border: `1px solid ${isSelected ? '#000' : '#eee'}`,
                                borderRadius: '4px',
                                backgroundColor: isSelected ? '#f9f9f9' : '#fff',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                position: 'relative'
                              }}
                            >
                              {/* Selection indicator */}
                              {isSelected && (
                                <div style={{
                                  position: 'absolute',
                                  top: '15px',
                                  right: '15px',
                                  width: '20px',
                                  height: '20px',
                                  borderRadius: '50%',
                                  backgroundColor: '#000',
                                  color: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '12px'
                                }}>
                                  ✓
                                </div>
                              )}
                              
                              <h5 style={{ 
                                margin: '0 0 8px 0', 
                                fontWeight: '500',
                                fontSize: '15px'
                              }}>
                                {warehouse.name}
                              </h5>
                              
                              <p style={{ 
                                margin: '0 0 5px 0',
                                fontSize: '13px',
                                color: '#666',
                                fontWeight: '300'
                              }}>
                                {warehouse.address}
                              </p>
                              
                              {warehouse.operationHoursStart && warehouse.operationHoursEnd && (
                                <div style={{ 
                                  fontSize: '12px',
                                  color: '#777',
                                  marginTop: '8px',
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: '15px'
                                }}>
                                  <span>Operating Hours: {warehouse.operationHoursStart.substring(0, 2)}:{warehouse.operationHoursStart.substring(2)} - {warehouse.operationHoursEnd.substring(0, 2)}:{warehouse.operationHoursEnd.substring(2)}</span>
                                  {warehouse.pocName && <span>Contact: {warehouse.pocName}, {warehouse.pocPhone}</span>}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ 
                        padding: '20px', 
                        textAlign: 'center', 
                        backgroundColor: '#f9f9f9',
                        borderRadius: '4px',
                        color: '#666'
                      }}>
                        No warehouses available
                      </div>
                    )}
                  </div>
                  
                  {apiError && (
                    <div style={{
                      color: '#d32f2f',
                      marginBottom: '15px',
                      padding: '8px',
                      backgroundColor: '#fff3f3',
                      borderRadius: '4px'
                    }}>
                      {apiError}
                    </div>
                  )}
                  <div style={{ 
                    display: 'flex',
                    gap: '10px',
                    justifyContent: 'flex-end',
                    marginTop: '20px'
                  }}>
                    <button 
                      type="button"
                      onClick={() => setIsConfirmModalOpen(false)}
                      disabled={isConfirmLoading}
                      style={{
                        padding: '8px 15px',
                        background: '#fff',
                        color: '#000',
                        border: '1px solid #000',
                        cursor: isConfirmLoading ? 'not-allowed' : 'pointer',
                        fontSize: '11px',
                        fontFamily: 'Helvetica, Arial, sans-serif',
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                        fontWeight: '400',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Cancel
                    </button>
                    <button 
                      type="button"
                      onClick={handleConfirmSchedule}
                      disabled={isConfirmLoading}
                      style={{
                        padding: '8px 15px',
                        background: '#000',
                        color: '#fff',
                        border: 'none',
                        cursor: isConfirmLoading ? 'not-allowed' : 'pointer',
                        fontSize: '11px',
                        fontFamily: 'Helvetica, Arial, sans-serif',
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                        fontWeight: '400',
                        transition: 'all 0.2s ease',
                        position: 'relative',
                        minWidth: '150px'
                      }}
                    >
                      {isConfirmLoading ? (
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ 
                            width: '12px', 
                            height: '12px', 
                            border: '2px solid rgba(255,255,255,0.3)', 
                            borderTop: '2px solid #fff',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            marginRight: '8px',
                            display: 'inline-block'
                          }}></span>
                          Processing...
                        </span>
                      ) : 'Confirm Schedule'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {isTrackingModalOpen && selectedOrder && (
            <div className="modal-overlay" onClick={(e) => {
              if (e.target === e.currentTarget) {
                setIsTrackingModalOpen(false);
              }
            }}>
              <div 
                className="modal-content" 
                style={{ 
                  maxWidth: '90%', 
                  width: '800px', 
                  height: '80vh',
                  overflowY: 'auto',
                  backgroundColor: 'white',
                  padding: '0px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  position: 'relative',
                  animation: 'slideIn 0.3s ease-out'
                }}
              >
                <div className="modal-header">
                  <h3 className="modal-title">TRACKING INFORMATION</h3>
                  <button className="modal-close-btn" onClick={() => setIsTrackingModalOpen(false)} aria-label="Close">
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  <div style={{ marginBottom: '40px' }}>
                    <p style={{ 
                      margin: '0 0 15px 0', 
                      fontWeight: '500', 
                      textTransform: 'uppercase', 
                      letterSpacing: '1px', 
                      fontSize: '11px',
                      borderBottom: '1px solid #eee',
                      paddingBottom: '10px'
                    }}>
                      ORDER ITEMS
                    </p>
                    
                    {selectedOrder.items && selectedOrder.items.map((item, index) => (
                      <div 
                        key={index} 
                        style={{ 
                          padding: '15px 0',
                          borderBottom: '1px solid #f5f5f5',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <p style={{ 
                            margin: '0 0 5px 0', 
                            fontWeight: '500',
                            fontSize: '14px'
                          }}>
                            {item.name}
                          </p>
                          <p style={{ 
                            margin: '0',
                            fontSize: '13px',
                            color: '#666',
                            fontWeight: '300'
                          }}>
                            Variant: {item.variant_title || 'Default'} • Qty: {item.quantity}
                          </p>
                          {item.shipping_info?.tracking_info?.awb_code && (
                            <p style={{ 
                              margin: '5px 0 0 0',
                              fontSize: '13px',
                              fontWeight: '300'
                            }}>
                              Tracking: {item.shipping_info.tracking_info.awb_code}
                            </p>
                          )}
                          {item.delivered_details && (
                            <p style={{ 
                              margin: '5px 0 0 0',
                              fontSize: '13px',
                              fontWeight: '500',
                              color: '#4CAF50'
                            }}>
                              Delivered on {new Date(item.delivered_details.delivered_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </p>
                          )}
                        </div>
                        
                        <div style={{ display: 'flex', gap: '10px' }}>
                          {item.shipping_info && (
                            <>
                              {item.shipping_info.shipping_documents ? (
                                <div style={{ 
                                  display: 'flex', 
                                  gap: '15px',
                                  alignItems: 'center'
                                }}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      downloadDocument(item.shipping_info.shipping_documents.manifest_url, `manifest-${item.shipping_info.shiprocket_shipment_id}.pdf`);
                                    }}
                                    style={{
                                      padding: '0',
                                      background: 'none',
                                      border: 'none',
                                      color: '#000',
                                      cursor: 'pointer',
                                      fontSize: '11px',
                                      fontFamily: 'Helvetica, Arial, sans-serif',
                                      letterSpacing: '1px',
                                      textTransform: 'uppercase',
                                      fontWeight: '400',
                                      textDecoration: 'underline'
                                    }}
                                  >
                                    Manifest
                                  </button>
                                  <span style={{ color: '#666' }}>•</span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      downloadDocument(item.shipping_info.shipping_documents.label_url, `label-${item.shipping_info.shiprocket_shipment_id}.pdf`);
                                    }}
                                    style={{
                                      padding: '0',
                                      background: 'none',
                                      border: 'none',
                                      color: '#000',
                                      cursor: 'pointer',
                                      fontSize: '11px',
                                      fontFamily: 'Helvetica, Arial, sans-serif',
                                      letterSpacing: '1px',
                                      textTransform: 'uppercase',
                                      fontWeight: '400',
                                      textDecoration: 'underline'
                                    }}
                                  >
                                    Label
                                  </button>
                                  <span style={{ color: '#666' }}>•</span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      downloadDocument(item.shipping_info.shipping_documents.invoice_url, `invoice-${item.shipping_info.shiprocket_shipment_id}.pdf`);
                                    }}
                                    style={{
                                      padding: '0',
                                      background: 'none',
                                      border: 'none',
                                      color: '#000',
                                      cursor: 'pointer',
                                      fontSize: '11px',
                                      fontFamily: 'Helvetica, Arial, sans-serif',
                                      letterSpacing: '1px',
                                      textTransform: 'uppercase',
                                      fontWeight: '400',
                                      textDecoration: 'underline'
                                    }}
                                  >
                                    Invoice
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    fetchShippingDocuments(selectedOrder.order_id, item.shipping_info.shiprocket_order_id, item.shipping_info.shiprocket_shipment_id);
                                  }}
                                  disabled={isDocumentLoading[item.shipping_info.shiprocket_shipment_id]}
                                  style={{
                                    padding: '8px 15px',
                                    background: '#fff',
                                    color: '#000',
                                    border: '1px solid #000',
                                    cursor: isDocumentLoading[item.shipping_info.shiprocket_shipment_id] ? 'not-allowed' : 'pointer',
                                    fontSize: '11px',
                                    fontFamily: 'Helvetica, Arial, sans-serif',
                                    letterSpacing: '1px',
                                    textTransform: 'uppercase',
                                    fontWeight: '400',
                                    transition: 'all 0.2s ease',
                                    whiteSpace: 'nowrap',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                  }}
                                >
                                  {isDocumentLoading[item.shipping_info.shiprocket_shipment_id] ? (
                                    <>
                                      <span style={{ 
                                        width: '12px', 
                                        height: '12px', 
                                        border: '2px solid rgba(0,0,0,0.3)', 
                                        borderTop: '2px solid #000',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite',
                                        display: 'inline-block'
                                      }}></span>
                                      Loading...
                                    </>
                                  ) : 'Shipping Documents'}
                                </button>
                              )}
                              
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  selectItemForTracking(item);
                                }}
                                style={{
                                  padding: '8px 15px',
                                  background: selectedItemForTracking === item ? '#000' : '#fff',
                                  color: selectedItemForTracking === item ? '#fff' : '#000',
                                  border: '1px solid #000',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  fontFamily: 'Helvetica, Arial, sans-serif',
                                  letterSpacing: '1px',
                                  textTransform: 'uppercase',
                                  fontWeight: '400',
                                  transition: 'all 0.2s ease',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {selectedItemForTracking === item ? 'SELECTED' : 'VIEW TRACKING'}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {selectedItemForTracking && selectedTrackingInfo && (
                    <>
                      <div style={{ marginBottom: '40px' }}>
                        <p style={{ 
                          margin: '0 0 15px 0', 
                          fontWeight: '500', 
                          textTransform: 'uppercase', 
                          letterSpacing: '1px', 
                          fontSize: '11px',
                          borderBottom: '1px solid #eee',
                          paddingBottom: '10px'
                        }}>
                          SHIPMENT DETAILS
                        </p>
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: '1fr 1fr', 
                          gap: '30px',
                          fontSize: '14px',
                          lineHeight: '1.5'
                        }}>
                          <div>
                            {selectedTrackingInfo.tracking_info?.awb_code && (
                              <div style={{ marginBottom: '20px' }}>
                                <p style={{ margin: '0 0 5px 0', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '11px' }}>
                                  AWB/TRACKING NUMBER
                                </p>
                                <p style={{ margin: '0', fontSize: '15px', fontWeight: '300' }}>
                                  {selectedTrackingInfo.tracking_info.awb_code}
                                </p>
                              </div>
                            )}
                            
                            {selectedTrackingInfo.shiprocket_order_id && (
                              <div style={{ marginBottom: '20px' }}>
                                <p style={{ margin: '0 0 5px 0', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '11px' }}>
                                  ORDER ID
                                </p>
                                <p style={{ margin: '0', fontSize: '15px', fontWeight: '300' }}>
                                  {selectedTrackingInfo.shiprocket_order_id}
                                </p>
                              </div>
                            )}
                            
                            {selectedTrackingInfo.warehouse_id && (
                              <div style={{ marginBottom: '20px' }}>
                                <p style={{ margin: '0 0 5px 0', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '11px' }}>
                                  WAREHOUSE ID
                                </p>
                                <p style={{ margin: '0', fontSize: '15px', fontWeight: '300' }}>
                                  {selectedTrackingInfo.warehouse_id}
                                </p>
                              </div>
                            )}
                          </div>
                          
                          <div>
                            {selectedTrackingInfo.tracking_info?.courier_name && (
                              <div style={{ marginBottom: '20px' }}>
                                <p style={{ margin: '0 0 5px 0', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '11px' }}>
                                  COURIER
                                </p>
                                <p style={{ margin: '0', fontSize: '15px', fontWeight: '300' }}>
                                  {selectedTrackingInfo.tracking_info.courier_name}
                                </p>
                              </div>
                            )}
                            
                            {selectedTrackingInfo.tracking_info?.invoice_no && (
                              <div style={{ marginBottom: '20px' }}>
                                <p style={{ margin: '0 0 5px 0', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '11px' }}>
                                  INVOICE
                                </p>
                                <p style={{ margin: '0', fontSize: '15px', fontWeight: '300' }}>
                                  {selectedTrackingInfo.tracking_info.invoice_no}
                                </p>
                              </div>
                            )}
                            
                            {selectedTrackingInfo.tracking_info?.applied_weight && (
                              <div style={{ marginBottom: '20px' }}>
                                <p style={{ margin: '0 0 5px 0', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '11px' }}>
                                  WEIGHT
                                </p>
                                <p style={{ margin: '0', fontSize: '15px', fontWeight: '300' }}>
                                  {selectedTrackingInfo.tracking_info.applied_weight} kg
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {selectedTrackingInfo.tracking_info?.pickup_details && (
                        <div style={{ marginBottom: '40px' }}>
                          <p style={{ 
                            margin: '0 0 15px 0', 
                            fontWeight: '500', 
                            textTransform: 'uppercase', 
                            letterSpacing: '1px', 
                            fontSize: '11px',
                            borderBottom: '1px solid #eee',
                            paddingBottom: '10px'
                          }}>
                            PICKUP DETAILS
                          </p>
                          
                          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                            {selectedTrackingInfo.tracking_info.pickup_details.response?.pickup_token_number && (
                              <div style={{ display: 'flex', marginBottom: '5px' }}>
                                <p style={{ 
                                  margin: '0', 
                                  fontWeight: '500', 
                                  width: '150px',
                                  fontSize: '13px'
                                }}>Reference:</p>
                                <p style={{ margin: '0', fontWeight: '300' }}>
                                  {selectedTrackingInfo.tracking_info.pickup_details.response.pickup_token_number}
                                </p>
                              </div>
                            )}
                            
                            {selectedTrackingInfo.tracking_info.pickup_details.response?.pickup_scheduled_date && (
                              <div style={{ display: 'flex', marginBottom: '5px' }}>
                                <p style={{ 
                                  margin: '0', 
                                  fontWeight: '500', 
                                  width: '150px',
                                  fontSize: '13px'
                                }}>Scheduled Date:</p>
                                <p style={{ margin: '0', fontWeight: '300' }}>
                                  {selectedTrackingInfo.tracking_info.pickup_details.response.pickup_scheduled_date}
                                </p>
                              </div>
                            )}
                            
                            {selectedTrackingInfo.tracking_info.pickup_details.response?.data && (
                              <div style={{ display: 'flex', marginBottom: '5px' }}>
                                <p style={{ 
                                  margin: '0', 
                                  fontWeight: '500', 
                                  width: '150px',
                                  fontSize: '13px'
                                }}>Status:</p>
                                <p style={{ margin: '0', fontWeight: '300' }}>
                                  {selectedTrackingInfo.tracking_info.pickup_details.response.data}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {selectedTrackingInfo.tracking_updates?.tracking_data && (
                        <div>
                          {/* Current Status Section */}
                          {selectedTrackingInfo.tracking_updates.tracking_data.shipment_track?.map((track, index) => (
                            <div key={index} style={{ marginBottom: '40px' }}>
                              <p style={{ 
                                margin: '0 0 15px 0', 
                                fontWeight: '500', 
                                textTransform: 'uppercase', 
                                letterSpacing: '1px', 
                                fontSize: '11px',
                                borderBottom: '1px solid #eee',
                                paddingBottom: '10px'
                              }}>
                                CURRENT STATUS
                              </p>
                              
                              <div style={{ 
                                display: 'flex',
                                justifyContent: 'space-between', 
                                fontSize: '15px',
                                marginBottom: '20px' 
                              }}>
                                <p style={{ 
                                  fontWeight: '600', 
                                  margin: '0',
                                  textTransform: 'uppercase',
                                  fontSize: '16px'
                                }}>
                                  {track.current_status}
                                </p>
                                
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: 'auto auto', 
                                    gap: '20px',
                                    fontSize: '14px'
                                  }}>
                                    <div>
                                      <p style={{ margin: '0 0 5px 0', fontWeight: '500' }}>Origin</p>
                                      <p style={{ margin: '0', fontWeight: '300' }}>{track.origin}</p>
                                    </div>
                                    <div>
                                      <p style={{ margin: '0 0 5px 0', fontWeight: '500' }}>Destination</p>
                                      <p style={{ margin: '0', fontWeight: '300' }}>{track.destination}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
                                gap: '20px',
                                fontSize: '14px'
                              }}>
                                {track.edd && (
                                  <div>
                                    <p style={{ 
                                      margin: '0 0 5px 0', 
                                      fontWeight: '500', 
                                      textTransform: 'uppercase', 
                                      letterSpacing: '1px', 
                                      fontSize: '11px' 
                                    }}>
                                      Expected Delivery
                                    </p>
                                    <p style={{ margin: '0', fontWeight: '300' }}>{track.edd}</p>
                                  </div>
                                )}
                                
                                {track.consignee_name && (
                                  <div>
                                    <p style={{ 
                                      margin: '0 0 5px 0', 
                                      fontWeight: '500', 
                                      textTransform: 'uppercase', 
                                      letterSpacing: '1px', 
                                      fontSize: '11px' 
                                    }}>
                                      Recipient
                                    </p>
                                    <p style={{ margin: '0', fontWeight: '300' }}>{track.consignee_name}</p>
                                  </div>
                                )}
                                
                                {track.pickup_date && (
                                  <div>
                                    <p style={{ 
                                      margin: '0 0 5px 0', 
                                      fontWeight: '500', 
                                      textTransform: 'uppercase', 
                                      letterSpacing: '1px', 
                                      fontSize: '11px' 
                                    }}>
                                      Pickup Date
                                    </p>
                                    <p style={{ margin: '0', fontWeight: '300' }}>{track.pickup_date}</p>
                                  </div>
                                )}
                                
                                {track.delivered_date && (
                                  <div>
                                    <p style={{ 
                                      margin: '0 0 5px 0', 
                                      fontWeight: '500', 
                                      textTransform: 'uppercase', 
                                      letterSpacing: '1px', 
                                      fontSize: '11px' 
                                    }}>
                                      Delivered Date
                                    </p>
                                    <p style={{ margin: '0', fontWeight: '300' }}>{track.delivered_date}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}

                          {/* Tracking Timeline */}
                          {selectedTrackingInfo.tracking_updates.tracking_data.shipment_track_activities && 
                           selectedTrackingInfo.tracking_updates.tracking_data.shipment_track_activities.length > 0 && (
                            <div>
                              <p style={{ 
                                margin: '0 0 25px 0', 
                                fontWeight: '500', 
                                textTransform: 'uppercase', 
                                letterSpacing: '1px', 
                                fontSize: '11px',
                                borderBottom: '1px solid #eee',
                                paddingBottom: '10px'
                              }}>
                                TRACKING HISTORY
                              </p>
                              
                              <div style={{ paddingLeft: '10px' }}>
                                {selectedTrackingInfo.tracking_updates.tracking_data.shipment_track_activities.map((activity, index) => {
                                  // Convert date to more readable format
                                  const activityDate = new Date(activity.date);
                                  const formattedDate = activityDate.toLocaleDateString('en-US', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric'
                                  });
                                  
                                  const formattedTime = activityDate.toLocaleTimeString('en-US', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true
                                  });
                                  
                                  const isFirst = index === 0;
                                  const isLast = index === selectedTrackingInfo.tracking_updates.tracking_data.shipment_track_activities.length - 1;
                                  
                                  return (
                                    <div key={index} style={{ 
                                      position: 'relative',
                                      paddingLeft: '30px',
                                      marginBottom: '30px',
                                      paddingBottom: isLast ? '0' : '30px',
                                      borderLeft: isLast ? 'none' : '1px solid #eee'
                                    }}>
                                      {/* Circle marker */}
                                      <div style={{
                                        position: 'absolute',
                                        left: '-5px',
                                        top: '0',
                                        width: '10px',
                                        height: '10px',
                                        borderRadius: '50%',
                                        backgroundColor: isFirst ? '#000' : '#eee',
                                        border: 'none'
                                      }}></div>
                                      
                                      <div style={{ marginBottom: '5px' }}>
                                        <p style={{ 
                                          margin: '0', 
                                          fontWeight: '500',
                                          fontSize: '14px',
                                          textTransform: isFirst ? 'uppercase' : 'none'
                                        }}>
                                          {activity.activity}
                                        </p>
                                        
                                        <div style={{ 
                                          display: 'flex',
                                          marginTop: '5px',
                                          fontSize: '13px',
                                          color: '#666',
                                          fontWeight: '300'
                                        }}>
                                          <p style={{ margin: '0 15px 0 0' }}>{formattedDate}</p>
                                          <p style={{ margin: '0' }}>{formattedTime}</p>
                                        </div>
                                      </div>
                                      
                                      {activity.location && (
                                        <p style={{ 
                                          margin: '10px 0 0 0',
                                          fontSize: '13px',
                                          color: '#666',
                                          fontWeight: '300'
                                        }}>
                                          {activity.location}
                                        </p>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          
                          {selectedTrackingInfo.tracking_updates.tracking_data.track_url && (
                            <div style={{ marginTop: '40px', textAlign: 'center' }}>
                              <a 
                                href={selectedTrackingInfo.tracking_updates.tracking_data.track_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{
                                  backgroundColor: '#000',
                                  color: '#fff',
                                  textDecoration: 'none',
                                  padding: '12px 40px',
                                  fontSize: '13px',
                                  letterSpacing: '1px',
                                  textTransform: 'uppercase',
                                  border: 'none',
                                  fontWeight: '400',
                                  cursor: 'pointer',
                                  display: 'inline-block'
                                }}
                              >
                                Track on Carrier Website
                              </a>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  
                  {!selectedItemForTracking && (
                    <div style={{ 
                      padding: '30px 0',
                      textAlign: 'center',
                      color: '#666',
                      fontWeight: '300',
                      fontSize: '14px'
                    }}>
                      Select an item to view its tracking information
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {isAdminModalOpen && selectedOrder && (
            <div className="modal-overlay" onClick={(e) => {
              if (e.target === e.currentTarget) {
                setIsAdminModalOpen(false);
              }
            }}>
              <div 
                className="modal-content" 
                style={{ 
                  maxWidth: '95%', 
                  width: '1200px', 
                  height: 'auto',
                  maxHeight: '90vh',
                  padding: '0px',
                  overflowY: 'auto',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  position: 'relative',
                  animation: 'slideIn 0.3s ease-out'
                }}
              >
                <div className="modal-header">
                  <h3 className="modal-title">Order Items - #{selectedOrder.order_id}</h3>
                  <button className="modal-close-btn" onClick={() => setIsAdminModalOpen(false)} aria-label="Close">
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
                    gap: '20px',
                    padding: '20px'
                  }}>
                    {selectedOrder.items && selectedOrder.items.map((item, index) => (
                      <div 
                        key={index}
                        onClick={() => handleItemClick(selectedOrder.order_id, item.product_id, item.variant_id)}
                        style={{
                          border: '1px solid #eee',
                          borderRadius: '8px',
                          padding: '20px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          backgroundColor: '#fff',
                          ':hover': {
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                            transform: 'translateY(-2px)'
                          }
                        }}
                      >
                        <div style={{ display: 'flex', gap: '20px', marginBottom: '15px' }}>
                          {/* Product Image */}
                          <div style={{ flexShrink: 0, width: '100px', height: '100px' }}>
                            {item.image ? (
                              <img 
                                src={item.image} 
                                alt={item.name}
                                style={{ 
                                  width: '100%', 
                                  height: '100%', 
                                  objectFit: 'cover',
                                  borderRadius: '4px' 
                                }}
                              />
                            ) : (
                              <div style={{ 
                                width: '100%', 
                                height: '100%', 
                                backgroundColor: '#f5f5f5', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                borderRadius: '4px',
                                color: '#aaa',
                                fontSize: '11px'
                              }}>
                                No Image
                              </div>
                            )}
                          </div>
                          
                          {/* Product Details */}
                          <div style={{ flex: 1 }}>
                            <p style={{ 
                              fontSize: '16px', 
                              fontWeight: '500', 
                              margin: '0 0 8px 0' 
                            }}>
                              {item.name || 'Unknown Product'}
                            </p>
                            <div style={{ 
                              fontSize: '14px',
                              color: '#666',
                              fontWeight: '300'
                            }}>
                              <p style={{ margin: '0 0 5px 0' }}>
                                <span style={{ fontWeight: '400' }}>Variant:</span> {item.variant_title || 'Default'}
                              </p>
                              <p style={{ margin: '0 0 5px 0' }}>
                                <span style={{ fontWeight: '400' }}>Quantity:</span> {item.quantity || 0}
                              </p>
                              <p style={{ margin: '0' }}>
                                <span style={{ fontWeight: '400' }}>Price:</span> ₹{item.price || '0'}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Status Indicators */}
                        <div style={{ 
                          display: 'flex',
                          gap: '10px',
                          marginTop: '15px',
                          paddingTop: '15px',
                          borderTop: '1px solid #f5f5f5'
                        }}>
                          {item.status === 'processed' && (
                            <span style={{
                              padding: '4px 8px',
                              backgroundColor: '#f8f8f8',
                              borderRadius: '4px',
                              fontSize: '12px',
                              color: '#666'
                            }}>
                              Processed
                            </span>
                          )}
                          {item.shipping_info && (
                            <span style={{
                              padding: '4px 8px',
                              backgroundColor: '#f8f8f8',
                              borderRadius: '4px',
                              fontSize: '12px',
                              color: '#666'
                            }}>
                              Shipped
                            </span>
                          )}
                          {item.delivered_details && (
                            <span style={{
                              padding: '4px 8px',
                              backgroundColor: '#e8f5e9',
                              borderRadius: '4px',
                              fontSize: '12px',
                              color: '#2e7d32'
                            }}>
                              Delivered
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Fulfillment; 